import { yupResolver } from "@hookform/resolvers/yup";
import {
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { AxiosResponse } from "axios";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import useCustomMemo from "../../Hooks/useCustomMemo";
import withRole from "../../Hooks/withRole";
import { getData } from "../../Services/getData";
import { postData } from "../../Services/postData";
import Config from "../../Utils/Config";
import {
  FormsIds,
  PaymentMethods,
  PaymentOptions,
  PaymentType,
} from "../../Utils/Constants";
import {
  AccountModel,
  InstructorModel,
  SalaryFormSchema,
  VehicleModel,
} from "../../Utils/Models";
import { NumericFormatCustom } from "../../Utils/NumericFormatComponents";
import { PaymentFormProps, PaymentFormType } from "../../Utils/Types";
import { endpoints } from "../../Utils/UrlEnum";
import { getFullName, onFormError } from "../../Utils/Utils";
import { Vocabulary } from "../../Utils/Vocabulary";
import CustomPanel from "../CustomPanel";
import { EditInstructor } from "../Orders/EditInstructor";
import { EditVehicle } from "../Orders/EditVehicle";

function PaymentForm(props: PaymentFormProps) {
  const { id } = useParams();
  const {
    instructor,
    onClose,
    paymentType,
    clients,
    showPaymentType,
    officeAssistants,
  } = props;
  const paymentMethods = props?.paymentMethods || Object.values(PaymentMethods);
  const paymentTypes = Object.values(PaymentOptions);
  const [instructors, setInstructors] = useState<InstructorModel[]>([]);
  const [vehicles, setVehicles] = useState([] as VehicleModel[]);
  const memo = useCustomMemo();
  const bankAccounts = memo?.storage.bankAccounts;
  const workPoints = memo?.storage.workPoints;
  const userWorkPoint = workPoints?.find(
    (wp: any) => wp?._id == localStorage.getItem("userWorkPointId")
  );

  /**
   * Create the form
   */
  const { control, getValues, setValue, handleSubmit } = useForm({
    resolver: yupResolver(SalaryFormSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      paymentMethod: PaymentMethods.CASH,
      instructorId: instructor,
      isRefund: false,
      account: bankAccounts?.find(
        (a: AccountModel) => a?.name == userWorkPoint?.name
      ),
    },
    shouldFocusError: true,
    shouldUnregister: false,
    criteriaMode: "firstError",
  });

  /**
   * Get the list of instructors and bank accounts
   */
  useEffect(() => {
    getData(
      `${endpoints.instructor.user}/paginated?page=0&perPage=100000`
    ).then((res: AxiosResponse) => {
      if (!res?.data) return;
      setInstructors(res.data?.results);
    });
  }, []);

  /**
   * Fetches and updates the list of vehicles associated with a specific instructor.
   *
   * @param instructorId - The unique identifier of the instructor whose vehicles are to be fetched.
   */
  const refreshVehicles = (instructorId: string) => {
    getData(
      `${endpoints.vehicle.main}/instructor/paginated?page=0&perPage=100000&id=${instructorId}`
    ).then((res: AxiosResponse) => {
      if (!res?.data) return;
      setVehicles(res.data);
    });
  };

  /**
   * Handles form submission for payment processing.
   *
   * @param data - The form data to be submitted for payment processing.
   * @returns A promise that resolves to a boolean indicating whether the submission was successful.
   */

  const onSubmit = async (data: any) => {
    //set the current order id and user id the current logged in user
    data.orderId = id;
    data.createdByUserId = localStorage.getItem("userId");
    data.userId =
      getValues("instructorId.user_id._id") ||
      getValues("instructorId.user_id");
    data.paymentType = PaymentType.PAYMENT;
    data.accountId = getValues("account._id");
    data.isSalary = paymentType == PaymentFormType.SALARY;
    data.officeAssistantId = getValues("userId");

    const url = `${endpoints.payments.main}`;
    const res = (await postData(url, data)) as any;
    if (!res || res?.error) {
      return false;
    }
    onClose();
    return true;
  };

  /**
   * Render the form
   */
  return (
    <div>
      <form
        id={FormsIds.salaryForm}
        onSubmit={handleSubmit(onSubmit, onFormError)}
      >
        <Grid container spacing={4}>
          {paymentType == PaymentFormType.PAYMENT && (
            <Grid item xs={12} sm={12} md={12}>
              <Controller
                name="clientId"
                control={control}
                render={({
                  field: { ref, ...field },
                  fieldState: { error },
                }) => (
                  <Autocomplete
                    disablePortal
                    clearOnBlur={true}
                    noOptionsText={Vocabulary.noResultsFound}
                    options={clients ?? []}
                    getOptionLabel={(option: any) =>
                      `${getFullName(option)} (${option.CNP})` || ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?._id == value?._id}
                    value={field.value}
                    onChange={(_, data) => field.onChange(data)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        value={field?.value || " "}
                        label={Vocabulary.cursant}
                        error={!!error}
                        helperText={error?.message}
                        variant="outlined"
                        fullWidth
                      />
                    )}
                  />
                )}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={12} md={6}>
            <Controller
              name="paymentMethod"
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Autocomplete
                  disablePortal
                  disableClearable
                  clearOnBlur={true}
                  options={paymentMethods ?? []}
                  getOptionLabel={(option: any) => option || ""}
                  isOptionEqualToValue={(option, value) => option == value}
                  value={field.value}
                  onChange={(_, data) => field.onChange(data)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      value={field?.value || " "}
                      label={Vocabulary.paymentMethod}
                      error={!!error}
                      helperText={error?.message}
                      variant="outlined"
                      fullWidth
                    />
                  )}
                />
              )}
            />
          </Grid>
          {showPaymentType && (
            <Grid item xs={12} sm={12} md={6}>
              <Controller
                name="isRefund"
                control={control}
                render={({
                  field: { ref, ...field },
                  fieldState: { error },
                }) => (
                  <Autocomplete
                    disablePortal
                    disableClearable
                    clearOnBlur={true}
                    options={paymentTypes ?? []}
                    getOptionLabel={(option: any) => option}
                    isOptionEqualToValue={(option, value) => {
                      return option == value;
                    }}
                    onChange={(_, data) =>
                      field.onChange(data == PaymentOptions.RETURN)
                    }
                    value={
                      field.value
                        ? PaymentOptions.RETURN
                        : PaymentOptions.NORMAL
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        value={field?.value || " "}
                        label={Vocabulary.paymentType}
                        error={!!error}
                        helperText={error?.message}
                        variant="outlined"
                        fullWidth
                      />
                    )}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={12} md={6}>
            <EditInstructor
              searchUrl={`${endpoints.instructor.user}/paginated?page=0&perPage=100000`}
              getValues={getValues}
              id="instructorId"
              control={control}
              onProductChange={(e, product, reason, details) => {
                const instructorDetails = details.option.instructorDetails;
                const instructorId = instructorDetails[0]._id;
                if (instructorDetails.length > 0) {
                  setValue(`instructorId`, instructorDetails[0]);
                }
                refreshVehicles(instructorId);
              }}
              instructors={instructors as any}
              applyStyle={false}
            />
          </Grid>
          {paymentType == PaymentFormType.PAYMENT && !instructor && (
            <Grid item xs={12} sm={12} md={6}>
              <EditVehicle
                getValues={getValues}
                control={control}
                id="vehicleId"
                searchUrl={`${endpoints.vehicle.main}/instructor/paginated?page=0&perPage=100000`}
                onProductChange={(e, product, reason, details) => {
                  setValue(`vehicleId`, details.option._id);
                }}
                vehicles={(vehicles as any)?.results}
                applyStyle={false}
              />
            </Grid>
          )}
          {paymentType == PaymentFormType.SALARY &&
            !props?.restrictByWorkPoint && (
              <Grid item xs={12} sm={12} md={6}>
                <Controller
                  name="userId"
                  control={control}
                  render={({
                    field: { ref, ...field },
                    fieldState: { error },
                  }) => (
                    <Autocomplete
                      disablePortal
                      clearOnBlur={true}
                      options={officeAssistants ?? []}
                      getOptionLabel={(option: any) =>
                        getFullName(option) || ""
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?._id == value?._id
                      }
                      value={field.value || null}
                      onChange={(_, data) => field.onChange(data)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          value={field?.value || " "}
                          label={Config.roles.OFFICE_ASSISTANT}
                          error={!!error}
                          helperText={error?.message}
                          variant="outlined"
                          fullWidth
                        />
                      )}
                    />
                  )}
                />
              </Grid>
            )}
          {!showPaymentType && (
            <Grid item xs={12} sm={12} md={6}>
              <Controller
                name="isRefund"
                control={control}
                render={({
                  field: { ref, ...field },
                  fieldState: { error },
                }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value} // very important
                      />
                    }
                    label={Vocabulary.refund}
                  />
                )}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={12} md={6}>
            <Controller
              name={"amount"}
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <TextField
                  {...field}
                  name="amount"
                  id="amount"
                  type="text"
                  label={Vocabulary.amountReceived}
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                  autoComplete="off"
                  InputProps={{
                    inputComponent: NumericFormatCustom,
                    // startAdornment: (
                    //   <InputAdornment position="start">
                    //     <LocalAtmIcon />
                    //   </InputAdornment>
                    // ),
                    endAdornment: (
                      <InputAdornment position="end">
                        {Vocabulary.currency}
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <Controller
              name="account"
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Autocomplete
                  disablePortal
                  disableClearable
                  clearOnBlur={true}
                  disabled={props?.restrictByWorkPoint}
                  options={bankAccounts ?? []}
                  getOptionLabel={(option: any) => option?.name || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?.name == value?.name
                  }
                  value={field.value}
                  onChange={(_, data) => field.onChange(data)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      value={field?.value || " "}
                      label={Vocabulary.account}
                      error={!!error}
                      helperText={error?.message}
                      variant="outlined"
                      fullWidth
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <Controller
              name={"description"}
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <TextField
                  {...field}
                  name="description"
                  id="description"
                  label={Vocabulary.paymentDescription}
                  fullWidth
                  multiline
                  rows={4}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <CustomPanel title={Vocabulary.observations}>
              <Typography>
                {"   "} Pentru{" "}
                <strong style={{ color: "red" }}>plata catre instructor</strong>{" "}
                se specifica <strong>ID-ul</strong> (din Bord al cursantului),
                instructorul, masina, metoda de plata, suma, comentarii.
              </Typography>
              <Typography>
                Ex: Platesc instructorul pentru motorina (specific ID-ul
                cursantului cu care a facut orele, specific instructorul si
                masina).
              </Typography>
            </CustomPanel>
          </Grid>
        </Grid>
      </form>
    </div>
  );
}

export default withRole(PaymentForm);
